import { AnimationDriver } from '@angular/animations/browser';
import { APP_BASE_HREF } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule, NgZone } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

import { animationPlayerFactory } from '../animation-player.factory';
import { AppTilesModule } from '../app-tiles/app-tiles.module';
import { EdgeworthLocalFilesService } from '../edgeworth/edgeworth-local-files.service';
import { PwaEdgeworthLocalFilesService } from '../edgeworth/pwa-edgeworth-local-files.service';
import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { FeedbackModule } from '../feedback/feedback.module';
import { FoldersModule } from '../folders/folders.module';
import { HeroModule } from '../hero/hero.module';
import { pwaOrderedHttpInterceptors } from '../http-interceptor-ordering/pwa.interceptor-ordering';
import { LazyViewModule } from '../lazy-view/lazy-view.module';
import { AppRouteReuseStrategy } from '../route-reuse-strategies/app.route-reuse-strategy';
import { DiskPreloadModule } from '../service-worker/disk-preload/disk-preload.module';
import { EncryptionService } from '../service-worker/encryption/encryption.service';
import { PwaServiceWorkerAuthorizedInstallService } from '../service-worker/registration-handlers/pwa-service-worker-authorized-install.service';
import { ServiceWorkerActivationTelemetryHandlerService } from '../service-worker/registration-handlers/service-worker-activation-telemetry-handler.service';
import { ServiceWorkerAppLoadService } from '../service-worker/registration-handlers/service-worker-app-load.service';
import { SERVICE_WORKER_REGISTRATION_HANDLERS } from '../service-worker/registration-handlers/service-worker-registration-handlers.injection-token';
import { ServiceWorkerUpdateContentHandlerService } from '../service-worker/registration-handlers/service-worker-update-content-handler.service';
import { UnregisterIfDisabledService } from '../service-worker/registration-handlers/unregister-if-disabled.service';
import { SERVICE_WORKER_GET_REGISTRATION_BY_SCOPE } from '../service-worker/service-worker-get-registration-by-scope.injection-token';
import { SERVICE_WORKER_USER_ID_FACTORY } from '../service-worker/service-worker-user-id.injection-token';
import { ServiceWorkerModule } from '../service-worker/service-worker.module';
import { fetchApiConfigurationDataFactory } from '../shared/bootstrap/fetch-api-configuration-data.factory';
import { shellHeight } from '../shared/constants/shell-height.constant';
import { ShellHeightToken } from '../shared/constants/shell-height.token';
import { WorkloadQueryParameter } from '../shared/constants/workload-query-parameter.enum';
import { WorkloadQueryParameterToken } from '../shared/constants/workload-query-parameter.injection-token';
import { SharedComponentsModule } from '../shared/directives/shared-components.module';
import { IconFontModule } from '../shared/icon-font/icon-font.module';
import { instrumentationQueueFactory } from '../shared/instrumentation/instrumentation-queue.factory';
import { INSTRUMENTATION_QUEUE } from '../shared/instrumentation/instrumentation-queue.injection-token';
import { InstrumentationModule } from '../shared/instrumentation/instrumentation.module';
import { InstrumentationService } from '../shared/instrumentation/instrumentation.service';
import { MruDataService } from '../shared/mru/mru-data.service';
import { ResizeModule } from '../shared/resize/resize.module';
import { AccessTokenService } from '../shared/services/access-token.service';
import { ApiConfigParserService } from '../shared/services/api-config-parser.service';
import { ApiConfigurationDataService } from '../shared/services/api-configuration-data.service';
import { BasicServicesModule } from '../shared/services/basic-services.module';
import { ConfigParserService } from '../shared/services/config-parser.service';
import { ConsumerLicenseService } from '../shared/services/consumer-license.service';
import { DataSecurityService } from '../shared/services/data-security.service';
import { DeviceDetector } from '../shared/services/device-detector.service';
import { ErrorDialogService } from '../shared/services/error-dialog.service';
import { FocusUpdateContentService } from '../shared/services/focus-update-content.service';
import { idleCallbackServiceFactory } from '../shared/services/idle-callback-service.factory';
import { IdleCallbackService } from '../shared/services/idle-callback.service';
import { JumpListService } from '../shared/services/jump-list.service';
import { LocalAppLauncherService } from '../shared/services/local-app-launcher.service';
import { OfficeHomeThemingService } from '../shared/services/office-home-theming.service';
import { performanceQueueFactory } from '../shared/services/performance-queue.factory';
import { PERFORMANCE_QUEUE } from '../shared/services/performance-queue.injection-token';
import { PinToTaskbarService } from '../shared/services/pin-to-taskbar.service';
import { ThemingService } from '../shared/services/theming.service';
import { UiHostBootstrapperService } from '../shared/services/ui-host-bootstrapper.service';
import { UpdateContentService } from '../shared/services/update-content.service';
import { UrlLauncherService } from '../shared/services/url-launcher.service';
import { UtilityService } from '../shared/services/utility.service';
import { WacBootstrapperService } from '../shared/services/wac-bootstrapper.service';
import { WindowUrlLauncherService } from '../shared/services/window-url-launcher.service';
import { WindowService } from '../shared/services/window.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { WRAPPER_VERSION } from '../shared/services/wrapper-version.token';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { SharePointModule } from '../sharepoint/sharepoint.module';
import { pwaServiceWorkerUserIdFactory } from './factories/pwa-service-worker-user-id.factory';
import { pwaWrapperVersionFactory } from './factories/pwa-wrapper-version.factory';
import { PwaRootComponent } from './pwa-root.component';
import { PwaRoutingModule } from './pwa-routing.module';
import { PwaModule } from './pwa.module';
import { AppServiceConnection } from './services/app-service-connection.service';
import { BulkLicensePreviewService } from './services/bulk-license-preview.service';
import { MockAppServiceConnection } from './services/mocks/mock-app-service-connection.service';
import { MockUrlLauncherService } from './services/mocks/mock-url-launcher.service';
import { PwaAccessTokenService } from './services/pwa-access-token.service';
import { PwaConfigService } from './services/pwa-config.service';
import { PwaDataSecurityService } from './services/pwa-data-security.service';
import { PwaIdleCallbackService } from './services/pwa-idle-callback.service';
import { PwaJumpListService } from './services/pwa-jump-list.service';
import { PwaLocalAppLauncherService } from './services/pwa-local-app-launcher.service';
import { PwaMruDataService } from './services/pwa-mru-data.service';
import { PwaPinToTaskbarService } from './services/pwa-pin-to-taskbar.service';
import { PwaUtilityService } from './services/pwa-utility.service';
import { PwaWorkloadsService } from './services/pwa-workloads.service';
import { UwpAppServiceConnection } from './services/uwp-app-service-connection.service';
import { UwpUrlLauncherService } from './services/uwp-url-launcher.service';

export function provideUrlLauncherService(capabilitiesService: CapabilitiesService, errorDialogService: ErrorDialogService, instrumentationService: InstrumentationService, pwaConfigService: PwaConfigService, windowUrlLauncherService: WindowUrlLauncherService): UrlLauncherService {
    return pwaConfigService.config.mock ? new MockUrlLauncherService(capabilitiesService, errorDialogService, instrumentationService, pwaConfigService, windowUrlLauncherService) : new UwpUrlLauncherService(capabilitiesService, errorDialogService, instrumentationService);
}

export function provideAppServiceConnection(clientStartupConfigService: ClientStartupConfigService, instrumentationService: InstrumentationService, pwaConfigService: PwaConfigService): AppServiceConnection {
    return pwaConfigService.config.mock ? new MockAppServiceConnection(pwaConfigService) : new UwpAppServiceConnection(clientStartupConfigService, instrumentationService, pwaConfigService);
}

export function provideIdleCallbackService(clientStartupConfigService: ClientStartupConfigService, deviceDetector: DeviceDetector, ngZone: NgZone, pwaConfigService: PwaConfigService, windowService: WindowService) : IdleCallbackService {
    return pwaConfigService.config.enablePwaIdleCallback && windowService.nativeWindow.MSApp ? new PwaIdleCallbackService(ngZone, windowService) : idleCallbackServiceFactory(clientStartupConfigService, deviceDetector);
}

@NgModule({
    declarations: [
        PwaRootComponent
    ],
    imports: [
        AppTilesModule,
        BasicServicesModule,
        BrowserAnimationsModule,
        BrowserModule,
        DiskPreloadModule,
        FeedbackModule,
        FoldersModule,
        HeroModule,
        HttpClientModule,
        IconFontModule,
        InstrumentationModule.forRoot(),
        LazyViewModule,
        PwaModule,
        PwaRoutingModule,
        ResizeModule,
        RouterModule,
        ServiceWorkerModule,
        SharedComponentsModule.forRoot(),
        SharePointModule
    ],
    providers: [
        CookieService,
        { provide: ShellHeightToken, useValue: shellHeight },
        { provide: ErrorHandler, useClass: OfficeHomePageErrorHandler },
        { provide: DataSecurityService, useClass: PwaDataSecurityService },
        { provide: AnimationDriver, useFactory: animationPlayerFactory, deps: [ClientStartupConfigService] },
        { provide: APP_BASE_HREF, useValue: '/' }, // This is needed in case <head> does not have <base href='/'> element: https://stackoverflow.com/questions/34535163/angular-2-router-no-base-href-set
        { provide: AccessTokenService, useClass: PwaAccessTokenService },
        { provide: ConfigParserService, useClass: ApiConfigParserService },
        { provide: APP_INITIALIZER, useFactory: fetchApiConfigurationDataFactory, multi: true, deps: [ApiConfigurationDataService] },
        { provide: WorkloadQueryParameterToken, useValue: WorkloadQueryParameter.Pwa },
        { provide: SERVICE_WORKER_REGISTRATION_HANDLERS, useClass: ServiceWorkerAppLoadService, multi: true },
        { provide: SERVICE_WORKER_REGISTRATION_HANDLERS, useClass: PwaServiceWorkerAuthorizedInstallService, multi: true },
        { provide: INSTRUMENTATION_QUEUE, useFactory: instrumentationQueueFactory },
        { provide: PERFORMANCE_QUEUE, useFactory: performanceQueueFactory },
        { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
        {
            provide: UrlLauncherService,
            useFactory: provideUrlLauncherService,
            deps: [CapabilitiesService, ErrorDialogService, InstrumentationService, PwaConfigService, WindowUrlLauncherService]
        },
        {
            provide: AppServiceConnection,
            useFactory: provideAppServiceConnection,
            deps: [ClientStartupConfigService, InstrumentationService, PwaConfigService]
        },
        {
            provide: IdleCallbackService,
            useFactory: provideIdleCallbackService,
            deps: [ClientStartupConfigService, DeviceDetector, NgZone, PwaConfigService, WindowService]
        },
        { provide: UiHostBootstrapperService, useClass: WacBootstrapperService },
        { provide: WRAPPER_VERSION, useFactory: pwaWrapperVersionFactory, deps: [PwaConfigService] },
        { provide: ConsumerLicenseService, useClass: BulkLicensePreviewService },
        { provide: JumpListService, useClass: PwaJumpListService },
        { provide: LocalAppLauncherService, useClass: PwaLocalAppLauncherService },
        { provide: MruDataService, useClass: PwaMruDataService },
        { provide: PinToTaskbarService, useClass: PwaPinToTaskbarService },
        { provide: UtilityService, useClass: PwaUtilityService },
        { provide: WorkloadsService, useClass: PwaWorkloadsService },
        { provide: UpdateContentService, useClass: FocusUpdateContentService },
        { provide: SERVICE_WORKER_USER_ID_FACTORY, useFactory: pwaServiceWorkerUserIdFactory, deps: [ WindowService ] },
        { provide: SERVICE_WORKER_REGISTRATION_HANDLERS, useClass: UnregisterIfDisabledService, multi: true },
        { provide: SERVICE_WORKER_REGISTRATION_HANDLERS, useClass: ServiceWorkerUpdateContentHandlerService, multi: true },
        { provide: SERVICE_WORKER_REGISTRATION_HANDLERS, useClass: ServiceWorkerActivationTelemetryHandlerService, multi: true },
        { provide: SERVICE_WORKER_GET_REGISTRATION_BY_SCOPE, useValue: true },
        { provide: EncryptionService, useValue: null }, // Override encryption service to be null to avoid key exceptions
        { provide: EdgeworthLocalFilesService, useClass: PwaEdgeworthLocalFilesService },
        { provide: ThemingService, useClass: OfficeHomeThemingService },
        pwaOrderedHttpInterceptors
    ],
    bootstrap: [
        PwaRootComponent
    ]
})
export class PwaRootModule { }
